import { Button, Col, Row, Spin } from 'antd';
import BaseSlider from './components/BaseSlider';
import placeholder from '../../../home/assets/images/placeholder.jpeg';
import userPlaceholder from '../../../user_profile/assets/images/placeholder_user.png';
import VerticalSliderCard from './components/VerticalSliderCard';

import './assets/css/sliders.css';
import HorizontalSliderImageCard from './components/HorizontalSliderImageCard';
import { RightOutlined } from '@ant-design/icons';
import AvatarSliderCard, {
  getUserSummaryLine,
} from './components/AvatarSliderCard';

import noImage from '../../../common/assets/images/no-photo.jpg';
import packageLeftIcon from '../sliders/assets/image/package_left-icon.svg';
import packageRightIcon from '../sliders/assets/image/package-right-icon.svg';
import breakfastIcon from './assets/image/croissant.svg';
import wifiIcon from './assets/image/wi-fi.svg';
import taxiIcon from './assets/image/location.svg';
import mealIcon from './assets/image/lunch.svg';
import { PlaceholderImage, SliderCardImage } from './components/SliderImages';
import { useState } from 'react';
import ImagesLightboxModal from '../../ImagesLightboxModal';
import { exotic_fishery } from '../../../common/Utils';

import CatchReportsDrawer from './components/CatchReportsDrawer';
import CatchReportCard from './components/CatchReportCard';

import { SwiperSlide } from 'swiper/react';
import { useHistory, useParams } from 'react-router';
import { useGetFisheryQuery } from '../../../fishery_profile/fisherySlice';

const HideLake1 = 'Suite Management';
const HideLake2 = 'The Fishing Lake';

function checkForEarlyReturn(loading, slidesData) {
  if (loading) {
    return (
      <div className={'txt-center mg-top-100 mg-bottom-100'}>
        <Spin />
      </div>
    );
  }

  if (!slidesData?.length) {
    return 'returnNull';
  }

  return false;
}

/**
 * Slider for a list of articles
 * @param articlesData {Array<{id: String, image?: String | Image, author: String, title: String, published_on: String}>} List of articles to be shown
 * @param loading {boolean} (optional) Whether to show the loading spinner instead of the slider
 * @returns {JSX.Element | null}
 */
export function ArticlesSlider({ articlesData, loading = false }) {
  let ordinal_suffix_of = require('../../../common/Utils').ordinal_suffix_of;
  let month = require('../../../common/Utils').month;

  const formatDate = (_date) => {
    let d = new Date(_date);
    let monthName = month[d.getMonth()];
    return (
      ' ' +
      ordinal_suffix_of(d.getDate()) +
      ' ' +
      monthName +
      ' ' +
      d.getFullYear()
    );
  };

  const returnEarly = checkForEarlyReturn(loading, articlesData);
  if (returnEarly) {
    return returnEarly === 'returnNull' ? null : returnEarly;
  }

  return (
    <BaseSlider className='limit-height'>
      {articlesData?.map((data, index) => (
        <Col className='slider-content' key={index} span={24}>
          <VerticalSliderCard
            href={`/article/${data.id}`}
            image={{
              src: data?.image || placeholder,
              alt: 'Featured image for ' + data.title,
            }}
            title={data.title}
            titleLimit={100}
            titleClass={'txt-single-line-ellipsis'}
            titleSize={16}
            postTitle={'Published on: ' + formatDate(data.published_on)}
            postTitleSize={12}
            extra={'Written by: ' + data.author}
          />
        </Col>
      ))}
    </BaseSlider>
  );
}

/**
 * Slider for a list of lakes
 * @param lakesData {Array<{public_id: number, name: String, lake_id: String, images: {primary: String, secondary: String[]}, price: any[], max_size: number, species: {name: String,weight: number,weight_unit: String}[],type: String,max_size_unit: String,access_type: String,description: String,allow_non_angling_guest: String,arrival_hour: number,departure_hour: number,max_rod: number,booking_type: String}>} List of lakes to be shown
 * @param fisheryPublicId {string} The public ID of the fishery which the lake belongs to (used for onClick URL)
 * @param loading {boolean} (optional) Whether to show the loading spinner instead of the slider
 * @returns {JSX.Element | null}
 */
export function LakesSlider({ lakesData, fisheryPublicId, loading = false }) {
  const returnEarly = checkForEarlyReturn(loading, lakesData);
  if (returnEarly) {
    return returnEarly === 'returnNull' ? null : returnEarly;
  }

  const breakpoints = {
    desktop: {
      breakpoint: { max: 10000, min: 1200 },
      items: 3,
      slidesToSlide: 2,
      partialVisibilityGutter: 30,
    },
    tablet: {
      breakpoint: { max: 1200, min: 670 },
      items: 2,
      slidesToSlide: 2,
      partialVisibilityGutter: 40,
    },
    mobile: {
      breakpoint: { max: 670, min: 300 },
      items: 1,
      slidesToSlide: 1,
      partialVisibilityGutter: 60,
    },
    smallMobile: {
      breakpoint: { max: 300, min: 0 },
      items: 1,
      slidesToSlide: 1,
      partialVisibilityGutter: 0,
    },
  };

  const idsToRemove = [HideLake1, HideLake2];

  const filteredItems =
    fisheryPublicId.toString() === exotic_fishery
      ? lakesData?.filter((item) => !idsToRemove.includes(item.name))
      : +fisheryPublicId === 12060
        ? lakesData?.slice(1)
        : lakesData;

  return (
    <BaseSlider
      horizontal
      extra={'Click tabs for details and imagery'}
      breakpointsOverride={breakpoints}
      noHoverAnimation
      extraClassName={'black mg-top-10 gMedium'}
    >
      {filteredItems?.map((data, index) => (
        <Col className='slider-content' key={index} span={24}>
          <HorizontalSliderImageCard
            href={`/fishery/${fisheryPublicId}/lake/${data.public_id}`}
            image={{
              src: data?.images ? data.images?.primary : placeholder,
              alt: 'Photo of ' + data.name + ' fishery',
            }}
            title={data.name}
            titleSize={18}
            extraClassName={'lake'}
          >
            <p className={'gRegular mg-bottom-5'}>
              <b className={'gSemiBold'}>Type:</b>{' '}
              {data?.accommodation ? 'Accommodation' : data.type}
            </p>
            <p className={'gRegular mg-bottom-5'}>
              <b className={'gSemiBold'}>Access:</b> {data.access_type}
            </p>
            {!data?.accommodation && (
              <p className={'gRegular mg-bottom-5'}>
                <b className={'gSemiBold'}>Max Size:</b>{' '}
                {data.max_size + ' ' + data.max_size_unit}
              </p>
            )}
          </HorizontalSliderImageCard>
        </Col>
      ))}
    </BaseSlider>
  );
}

export function CatchReportSlider({
  isHomePage = false,
  lakesData,
  fisheryPublicId,
  loading = false,
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [moreDetailOpen, setMoreDetailOpen] = useState(false);
  const [catchImage, setCatchImage] = useState(false);
  const [catchDetails, setCatchDetails] = useState([]);

  const openCatchDetails = (data) => {
    setCatchImage([{ src: data?.image_upload || noImage, alt: 'catch-image' }]);
    setCatchDetails(data);
    setMoreDetailOpen(true);
  };

  const returnEarly = checkForEarlyReturn(loading, lakesData);
  if (returnEarly) {
    return returnEarly === 'returnNull' ? null : returnEarly;
  }

  const breakpoints = {
    340: {
      slidesPerView: 1.2,
    },
    768: {
      slidesPerView: 2.2,
    },
    1224: {
      slidesPerView: 3.3,
    },
  };

  const idsToRemove = [HideLake1, HideLake2];

  const filteredItems =
    fisheryPublicId?.toString() === exotic_fishery
      ? lakesData?.filter((item) => !idsToRemove.includes(item.name))
      : lakesData;

  return (
    <>
      <ImagesLightboxModal
        isOpen={lightboxOpen}
        onClose={() => {
          setLightboxOpen(false);
          setCatchImage([]);
        }}
        infinite={false}
        images={catchImage}
        loading={!catchImage}
      />
      <CatchReportsDrawer
        drawerOpen={moreDetailOpen}
        setIsDrawerOpen={setMoreDetailOpen}
        catchDetails={catchDetails}
      />

      <BaseSlider
        horizontal
        extra={''}
        isSwiperJs
        swiperSpaceBetween={20}
        breakpointsOverride={breakpoints}
        noHoverAnimation
        hideButtonsOnMobile={true}
        showPartialNext={isHomePage}
        className={`catch-slider homepage-catch-slider swiper-version`}
        extraClassName={'black mg-top-10 gMedium'}
      >
        {filteredItems?.map((data, index) =>
          isHomePage ? (
            <SwiperSlide key={data.id ?? index} className='cp'>
              <div
                className='slider-content'
                onClick={() => openCatchDetails(data)}
              >
                <SliderCardImage
                  src={data?.image_upload || noImage}
                  alt='catch-image'
                />
                {data?.user_subscription_level === 'Pro' && (
                  <div className='slider-pro-icon sb-blue-bg-800 font-montserrat'>
                    PRO
                  </div>
                )}
              </div>
              <div className='mg-left-10 mg-right-10 mg-top-20'>
                <div className='fs-16 gMedium grey-700'>{data?.name}</div>
                <div className='fs-20 gBold grey-900'>{data?.fishery}</div>
              </div>
            </SwiperSlide>
          ) : (
            <SwiperSlide
              key={data.id ?? index}
              className='cp'
              onClick={() => openCatchDetails(data)}
            >
              <CatchReportCard
                report={data}
                onViewDetails={() => openCatchDetails(data)}
              />
            </SwiperSlide>
          )
        )}
      </BaseSlider>
    </>
  );
}
export function PackagesSlider({
  lakesData,
  fisheryPublicId,
  activeLanguage,
  loading = false,
  showFishery = false,
}) {
  const history = useHistory();
  const { data: fisheryData } = useGetFisheryQuery(fisheryPublicId);

  const packageId = useParams()['package_id'];
  const returnEarly = checkForEarlyReturn(loading, lakesData);
  if (returnEarly) {
    return returnEarly === 'returnNull' ? null : returnEarly;
  }

  const breakpoints = {
    340: {
      slidesPerView: 1,
    },
    768: {
      slidesPerView: 2,
    },
    1224: {
      slidesPerView: 3,
    },
  };
  const filteredItems = lakesData?.filter(
    (item) => item?.show_package_online && item?.id !== packageId
  );

  return (
    <>
      <BaseSlider
        horizontal
        extra={''}
        isSwiperJs
        swiperSpaceBetween={0}
        breakpointsOverride={breakpoints}
        noHoverAnimation
        ButtonLeftIcon={packageLeftIcon}
        ButtonRightIcon={packageRightIcon}
        hideButtonsOnMobile={true}
        isPackages={true}
        // showPartialNext={false}
        className={`package-slider  swiper-version`}
        extraClassName={'black mg-top-10 gMedium'}
      >
        {filteredItems?.map((item, index) => (
          <SwiperSlide className='cp'>
            <div
              className='slider-content'
              key={filteredItems?.id}
              span={24}
              onClick={(e) => {}}
            >
              <Col className='h-full'>
                <div className='package-container'>
                  <Row className='package-content gap-1r w-full h-full'>
                    <Col
                      xs={{ order: 2, span: 24 }}
                      sm={{ span: 15, order: 1 }}
                      className='package-details'
                    >
                      <div className='mg-bottom-10 row-center flex-wrap gap-05r'>
                        <div className='package-title sb-blue-700 fs-12  font-montserrat'>
                          {item?.name}
                        </div>
                        <div className='gBold  fs-10 row-center fs-10 mg-left-10'>
                          {item?.package_type === 'last_minute' ||
                          item?.package_type === 'early_bird' ? (
                            <span
                              className=' fs-10 txt-center '
                              style={{
                                borderRadius: '100px',
                                background: '#FFD98F',
                                color: '#5C3D00',
                                width: 120,
                                padding: '2px 12px',
                              }}
                            >
                              {
                                activeLanguage?.reservation?.[
                                  item?.package_type
                                ]
                              }
                            </span>
                          ) : item?.package_type === 'specific_date_range' ? (
                            <span
                              className=' fs-10 txt-center '
                              style={{
                                borderRadius: '100px',
                                background: '#D9EEFF',
                                color: '#0D3759',
                                width: 120,
                                padding: '2px 12px',
                              }}
                            >
                              {activeLanguage?.reservation?.date_range}
                            </span>
                          ) : (
                            ''
                          )}
                        </div>
                      </div>
                      <div className='package-features fs-10 gMedium sb-blue-700'>
                        {item?.included_benefits
                          .filter((b) => b.selected)
                          .slice(0, 4)
                          .map((benefit) => (
                            <div
                              className='row-center'
                              style={{ flex: '1 1 45%' }}
                            >
                              <img
                                src={
                                  benefit?.label === 'Daily Breakfast'
                                    ? breakfastIcon
                                    : benefit?.label === 'Internet'
                                      ? wifiIcon
                                      : benefit?.label === 'Pickup Included' ||
                                          benefit?.label === 'Dropoff Included'
                                        ? taxiIcon
                                        : benefit?.label === 'Meals Included'
                                          ? mealIcon
                                          : ''
                                }
                                className=' mg-right-10'
                                style={{ width: 12, height: 12 }}
                              ></img>{' '}
                              {benefit?.customText
                                ? benefit?.customText
                                : benefit?.label}
                            </div>
                          ))}
                      </div>
                      <div className='package-price-container fs-10 row-center justify-between'>
                        <span className='package-price-label uppercase font-montserrat sb-blue-700'>
                          {activeLanguage?.reservation?.packagePrice}
                        </span>
                        <div className='package-price sb-blue-bg-100 fs-10 font-montserrat sb-blue-700'>
                          {item?.currency}
                          {item?.convertedAmount ??
                            item?.final_price_in_pence / 100}
                        </div>
                      </div>

                      <div className='package-buttons'>
                        <Button
                          className='view-button uppercase fs-12'
                          onClick={(e) => {
                            window.scrollTo(0, 0);
                            history.push(
                              `/booking/package_details/${fisheryPublicId}/${item?.id}`
                            );
                          }}
                        >
                          {activeLanguage?.reservation?.viewPackage}
                        </Button>
                      </div>
                    </Col>
                    <Col
                      xs={{ order: 1, span: 24 }}
                      sm={{ span: 9, order: 2 }}
                      className='package-image-container row-center'
                    >
                      {item?.package_image ? (
                        <img
                          src={item?.package_image}
                          alt='Fishing Package'
                          className='package-image'
                        />
                      ) : (
                        <div className='package-image sb-blue-bg-600 white fs-20 gSemiBold justify-center center'>
                          No Image
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              </Col>
            </div>
          </SwiperSlide>
        ))}
      </BaseSlider>
    </>
  );
}

/**
 * Slider for a list of reviews
 * @param reviewsData {Array<{comment: String, commented_at: String, first_name: String, last_name: String, personal_best:{species: String, unit: String, weight: String}, profile_image: String, years_angling: String}>} List of reviews to be shown
 * @param fisheryPublicId {string} The public ID of the fishery which the reviews relate to (used for onClick URL)
 * @param loading {boolean} (optional) Whether to show the loading spinner instead of the slider
 * @returns {JSX.Element | null}
 */
export function ReviewsSlider({
  reviewsData,
  fisheryPublicId,
  loading = false,
}) {
  const returnEarly = checkForEarlyReturn(loading, reviewsData);
  if (returnEarly) {
    return returnEarly === 'returnNull' ? null : returnEarly;
  }

  return (
    <BaseSlider
      horizontal
      extra={null}
      showPartialNext
      noHoverAnimation
      hideButtonsOnMobile
      infinite={false}
    >
      {reviewsData?.map((data, index) => (
        <Col className='slider-content' key={index} span={24}>
          <AvatarSliderCard
            href={`/fishery/${fisheryPublicId}/reviews/${index + 1}`}
            image={{
              src: data?.profile_image || userPlaceholder,
              alt: data.first_name + ' ' + data.last_name,
            }}
            title={data.first_name + ' ' + data.last_name}
            postTitle={getUserSummaryLine(data)}
            extra={
              <>
                Show more <RightOutlined />
              </>
            }
          >
            <p className={'gRegular mg-bottom-5 review-slider-preview'}>
              {data.comment}
            </p>
          </AvatarSliderCard>
        </Col>
      ))}
    </BaseSlider>
  );
}

/**
 * A full-width slider for images
 * @param images {Array<{src: String, alt: String}>} List of images to be shown
 * @param loading {boolean} (optional) Whether to show the loading spinner instead of the slider
 * @param openOnClick {boolean} (optional) Whether to open the image in a zoomable lightbox modal when clicked
 * @param height {String} (optional) The height of the slider (used for preventing content movement during image loading)
 * @param showButtons {boolean} (optional) Whether to show the left-right buttons
 * @param showDots {boolean} (optional) Whether to show the slider dots
 * @param hideImageCount {boolean} (optional) Whether to hide the count overlay on each image (e.g. '2/5')
 * @param infinite {boolean} (optional) Whether the slider repeats (is an infinite loop) -- default: true. Note: this only works if initialIndex == 0
 * @param initialIndex {number} (optional) The index of the slide to display first (0 by default)
 * @param onChange {(currentIndex: number) => void} (optional) A function to carry out when the slider changes (index may be inaccurate when infinite=true)
 * @returns {JSX.Element | null}
 */
export function ImageSlider({
  images,
  loading = false,
  height = '20rem',
  showButtons = false,
  showDots = false,
  hideImageCount = false,
  initialIndex = 0,
  openOnClick = false,
  infinite = false,
  onChange = (currentIndex) => {},
}) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const returnEarly = checkForEarlyReturn(loading, images);
  if (returnEarly) {
    return (
      <div style={{ height: height }}>
        {returnEarly === 'returnNull' ? null : returnEarly}
      </div>
    );
  }

  return (
    <div
      style={{ height: height }}
      className={
        'image_slider ' + (openOnClick ? ' image_slider__clickable ' : '')
      }
    >
      <BaseSlider
        fullWidth
        hideButtonsOnMobile
        extra={null}
        hideButtons={!showButtons || images.length <= 1}
        onChange={onChange}
        showDots={showDots}
        infinite={
          /* Must be true if initialIndex is non-zero otherwise the image numbering won't make sense  */
          initialIndex ? true : infinite
        }
      >
        {[...images.slice(initialIndex), ...images.slice(0, initialIndex)]?.map(
          (data, index) => (
            <Col
              className='slider-content'
              key={index}
              span={24}
              style={{ height: height }}
              onClick={() => openOnClick && setLightboxIndex(index)}
            >
              <PlaceholderImage
                src={data?.src}
                alt={data.alt}
                className={'image_slider_image'}
              />
              {!hideImageCount && (
                <div className={'slider-count'}>
                  {((initialIndex + index) % images.length) + 1} /{' '}
                  {images.length}
                </div>
              )}
            </Col>
          )
        )}
      </BaseSlider>
      {openOnClick && (
        <ImagesLightboxModal
          isOpen={lightboxIndex !== null}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          images={images}
          loading={loading}
        />
      )}
    </div>
  );
}
